// Static UI strings used by the patient-facing questionnaire flow. Template
// content (questions, options, instructions, consent_text) is translated
// server-side via Gemini (see backend/translation_service.py). This dict
// only covers CHROME: buttons, progress labels, success/error screens,
// consent gate wording.
//
// Fallback strategy: if a key is missing for `locale`, we fall back to `en`,
// then finally to French (the source of truth).

export const PATIENT_LOCALES = ["fr", "en", "es", "de", "it", "pt", "ar", "zh"];

const STRINGS = {
  fr: {
    previous: "Précédent",
    next: "Suivant",
    finishStep: "Terminer",
    start: "Commencer",
    submit: "Envoyer mes réponses",
    submitting: "Envoi…",
    saveScore: "Enregistrer le score",
    refuse: "Refuser",
    acceptAndContinue: "J'accepte et je continue →",
    yes: "Oui",
    no: "Non",
    minLabel: "Min",
    maxLabel: "Max",
    questionOf: (i, total) => `Question ${i} / ${total}`,
    lastStep: "Dernière étape",
    almostThere: "Vous y êtes presque !",
    saving: "Enregistrement",
    saveIntroDirect: (name) => `Cliquez pour enregistrer les réponses de ${name} dans son dossier.`,
    submitIntro: "Cliquez sur « Envoyer mes réponses » pour transmettre vos réponses à votre praticien.",
    draftSaved: "Enregistré · vous pouvez fermer et revenir plus tard",
    instructionsForThisQuestion: "Consignes de cette question",
    thanks: "Merci !",
    thanksBody: "Vos réponses ont bien été transmises à votre praticien.",
    completedAt: (dateStr, timeStr) => `Questionnaire terminé le ${dateStr} à ${timeStr}`,
    translating: "Traduction…",
    linkInvalid: "Lien invalide",
    templateDeletedTitle: "Votre praticien a supprimé ce questionnaire",
    templateDeletedBody: "Ce lien n'est plus actif. Contactez votre praticien si vous pensez qu'il s'agit d'une erreur — il pourra vous envoyer un nouveau questionnaire.",
    consentRefusedTitle: "Consentement refusé",
    consentRefusedBody: "Votre choix a bien été enregistré. Votre praticien sera notifié et pourra vous contacter par un autre moyen si nécessaire.",
    // Consent gate (step === -2)
    consentEyebrow: "Questionnaire médical",
    consentTitleSuffix: "vous invite à remplir un questionnaire",
    consentSubtitle: "Vos réponses lui seront transmises directement pour préparer votre prise en charge. Cela ne prend que quelques minutes.",
    dataProtectedTitle: "Vos données de santé sont protégées",
    dataProtectedBody: "Elles sont hébergées en France chez un hébergeur agréé HDS et ne sont accessibles qu'à votre praticien.",
    consentCheckboxLabel: "J'accepte la {privacy} et que mes réponses soient transmises à mon praticien pour ma prise en charge médicale.",
    privacyLinkLabel: "politique de données personnelles",
    consentFooter: "Votre consentement est horodaté et tracé conformément au RGPD (Art. 7.1). Vous pouvez le retirer à tout moment auprès de votre praticien.",
    // Intro screen (step === -1)
    introEyebrow: "Questionnaire",
    introDirect: (prac, patient) => ["Rempli en ", "consultation", " par ", prac, " pour ", patient, "."],
    introRemote: (prac) => ["Envoyé par ", prac, "."],
    questionsCount: (n) => `${n} question${n > 1 ? "s" : ""}`,
    minutes: (m) => `~${m} min`,
    resumeTitle: "Reprise de votre questionnaire",
    resumeBody: (n) => `Vous avez déjà répondu à ${n} question${n > 1 ? "s" : ""}. Cliquez sur « Commencer » pour reprendre là où vous vous êtes arrêté(e).`,
    pauseTipStrong: "Vous pouvez faire une pause à tout moment.",
    pauseTipBody: "Vos réponses sont sauvegardées automatiquement. Fermez simplement cette page et rouvrez le lien de votre email pour reprendre.",
    cguEyebrow: "Conditions générales d'utilisation",
    cguText: "J'ai lu et j'accepte les conditions générales d'utilisation de Questiomed.",
    readCgu: "Lire les conditions générales →",
  },
  en: {
    previous: "Previous",
    next: "Next",
    finishStep: "Finish",
    start: "Start",
    submit: "Send my answers",
    submitting: "Sending…",
    saveScore: "Save score",
    refuse: "Decline",
    acceptAndContinue: "I accept and continue →",
    yes: "Yes",
    no: "No",
    minLabel: "Min",
    maxLabel: "Max",
    questionOf: (i, total) => `Question ${i} / ${total}`,
    lastStep: "Last step",
    almostThere: "Almost there!",
    saving: "Saving",
    saveIntroDirect: (name) => `Click to save ${name}'s answers to their record.`,
    submitIntro: "Click “Send my answers” to transmit your answers to your practitioner.",
    draftSaved: "Saved · you can close and come back later",
    instructionsForThisQuestion: "Instructions for this question",
    thanks: "Thank you!",
    thanksBody: "Your answers have been sent to your practitioner.",
    completedAt: (dateStr, timeStr) => `Questionnaire completed on ${dateStr} at ${timeStr}`,
    translating: "Translating…",
    linkInvalid: "Invalid link",
    templateDeletedTitle: "Your practitioner has deleted this questionnaire",
    templateDeletedBody: "This link is no longer active. Contact your practitioner if you believe this is an error — they can send you a new questionnaire.",
    consentRefusedTitle: "Consent declined",
    consentRefusedBody: "Your choice has been recorded. Your practitioner will be notified and can contact you by another means if needed.",
    consentEyebrow: "Medical questionnaire",
    consentTitleSuffix: "invites you to complete a questionnaire",
    consentSubtitle: "Your answers will be sent directly to prepare your care. It only takes a few minutes.",
    dataProtectedTitle: "Your health data is protected",
    dataProtectedBody: "It is hosted in France by an HDS-certified provider and can only be accessed by your practitioner.",
    consentCheckboxLabel: "I accept the {privacy} and that my answers be transmitted to my practitioner for my medical care.",
    privacyLinkLabel: "personal data policy",
    consentFooter: "Your consent is timestamped and tracked in accordance with GDPR (Art. 7.1). You can withdraw it at any time from your practitioner.",
    introEyebrow: "Questionnaire",
    introDirect: (prac, patient) => ["Filled in ", "consultation", " by ", prac, " for ", patient, "."],
    introRemote: (prac) => ["Sent by ", prac, "."],
    questionsCount: (n) => `${n} question${n > 1 ? "s" : ""}`,
    minutes: (m) => `~${m} min`,
    resumeTitle: "Resuming your questionnaire",
    resumeBody: (n) => `You have already answered ${n} question${n > 1 ? "s" : ""}. Click “Start” to resume where you left off.`,
    pauseTipStrong: "You can pause at any time.",
    pauseTipBody: "Your answers are saved automatically. Just close this page and reopen the link in your email to resume.",
    cguEyebrow: "Terms of use",
    cguText: "I have read and accept the Questiomed terms of use.",
    readCgu: "Read the terms →",
  },
  es: {
    previous: "Anterior",
    next: "Siguiente",
    finishStep: "Finalizar",
    start: "Empezar",
    submit: "Enviar mis respuestas",
    submitting: "Enviando…",
    saveScore: "Guardar puntuación",
    refuse: "Rechazar",
    acceptAndContinue: "Acepto y continúo →",
    yes: "Sí",
    no: "No",
    minLabel: "Mín",
    maxLabel: "Máx",
    questionOf: (i, total) => `Pregunta ${i} / ${total}`,
    lastStep: "Último paso",
    almostThere: "¡Ya casi está!",
    saving: "Guardando",
    saveIntroDirect: (name) => `Haga clic para guardar las respuestas de ${name} en su expediente.`,
    submitIntro: "Haga clic en «Enviar mis respuestas» para transmitir sus respuestas a su profesional.",
    draftSaved: "Guardado · puede cerrar y volver más tarde",
    instructionsForThisQuestion: "Instrucciones para esta pregunta",
    thanks: "¡Gracias!",
    thanksBody: "Sus respuestas se han enviado a su profesional.",
    completedAt: (dateStr, timeStr) => `Cuestionario completado el ${dateStr} a las ${timeStr}`,
    translating: "Traduciendo…",
    linkInvalid: "Enlace no válido",
    templateDeletedTitle: "Su profesional ha eliminado este cuestionario",
    templateDeletedBody: "Este enlace ya no está activo. Contacte con su profesional si cree que se trata de un error — podrá enviarle un nuevo cuestionario.",
    consentRefusedTitle: "Consentimiento rechazado",
    consentRefusedBody: "Su elección ha sido registrada. Su profesional será notificado y podrá contactarle por otro medio si es necesario.",
    consentEyebrow: "Cuestionario médico",
    consentTitleSuffix: "le invita a completar un cuestionario",
    consentSubtitle: "Sus respuestas le serán transmitidas directamente para preparar su atención. Sólo tarda unos minutos.",
    dataProtectedTitle: "Sus datos de salud están protegidos",
    dataProtectedBody: "Están alojados en Francia por un proveedor certificado HDS y sólo son accesibles a su profesional.",
    consentCheckboxLabel: "Acepto la {privacy} y que mis respuestas sean transmitidas a mi profesional para mi atención médica.",
    privacyLinkLabel: "política de datos personales",
    consentFooter: "Su consentimiento queda con fecha y hora y trazado conforme al RGPD (Art. 7.1). Puede retirarlo en cualquier momento ante su profesional.",
    introEyebrow: "Cuestionario",
    introDirect: (prac, patient) => ["Rellenado en ", "consulta", " por ", prac, " para ", patient, "."],
    introRemote: (prac) => ["Enviado por ", prac, "."],
    questionsCount: (n) => `${n} pregunta${n > 1 ? "s" : ""}`,
    minutes: (m) => `~${m} min`,
    resumeTitle: "Reanudando su cuestionario",
    resumeBody: (n) => `Ya ha respondido a ${n} pregunta${n > 1 ? "s" : ""}. Haga clic en «Empezar» para reanudar donde lo dejó.`,
    pauseTipStrong: "Puede hacer una pausa en cualquier momento.",
    pauseTipBody: "Sus respuestas se guardan automáticamente. Simplemente cierre esta página y vuelva a abrir el enlace de su correo para reanudar.",
    cguEyebrow: "Condiciones generales de uso",
    cguText: "He leído y acepto las condiciones generales de uso de Questiomed.",
    readCgu: "Leer las condiciones →",
  },
  de: {
    previous: "Zurück",
    next: "Weiter",
    finishStep: "Abschließen",
    start: "Beginnen",
    submit: "Antworten senden",
    submitting: "Wird gesendet…",
    saveScore: "Punktzahl speichern",
    refuse: "Ablehnen",
    acceptAndContinue: "Ich akzeptiere und fahre fort →",
    yes: "Ja",
    no: "Nein",
    minLabel: "Min",
    maxLabel: "Max",
    questionOf: (i, total) => `Frage ${i} / ${total}`,
    lastStep: "Letzter Schritt",
    almostThere: "Fast geschafft!",
    saving: "Speichern",
    saveIntroDirect: (name) => `Klicken Sie, um die Antworten von ${name} in der Akte zu speichern.`,
    submitIntro: "Klicken Sie auf „Antworten senden“, um Ihre Antworten an Ihre Behandelnde/n zu übermitteln.",
    draftSaved: "Gespeichert · Sie können schließen und später zurückkehren",
    instructionsForThisQuestion: "Hinweise zu dieser Frage",
    thanks: "Danke!",
    thanksBody: "Ihre Antworten wurden an Ihre Behandelnde/n gesendet.",
    completedAt: (dateStr, timeStr) => `Fragebogen abgeschlossen am ${dateStr} um ${timeStr}`,
    translating: "Übersetzen…",
    linkInvalid: "Ungültiger Link",
    templateDeletedTitle: "Ihre Behandelnde/r hat diesen Fragebogen gelöscht",
    templateDeletedBody: "Dieser Link ist nicht mehr aktiv. Wenden Sie sich an Ihre Behandelnde/n, wenn Sie glauben, dass es sich um einen Fehler handelt — sie können Ihnen einen neuen Fragebogen senden.",
    consentRefusedTitle: "Einwilligung verweigert",
    consentRefusedBody: "Ihre Wahl wurde registriert. Ihre Behandelnde/r wird benachrichtigt und kann Sie bei Bedarf auf andere Weise kontaktieren.",
    consentEyebrow: "Medizinischer Fragebogen",
    consentTitleSuffix: "lädt Sie zum Ausfüllen eines Fragebogens ein",
    consentSubtitle: "Ihre Antworten werden direkt weitergeleitet, um Ihre Versorgung vorzubereiten. Dauert nur wenige Minuten.",
    dataProtectedTitle: "Ihre Gesundheitsdaten sind geschützt",
    dataProtectedBody: "Sie werden in Frankreich von einem HDS-zertifizierten Anbieter gehostet und sind nur für Ihre Behandelnde/n zugänglich.",
    consentCheckboxLabel: "Ich stimme der {privacy} zu und dass meine Antworten an meine Behandelnde/n zur medizinischen Versorgung übermittelt werden.",
    privacyLinkLabel: "Datenschutzrichtlinie",
    consentFooter: "Ihre Einwilligung wird gemäß DSGVO (Art. 7.1) mit Zeitstempel und Nachverfolgung dokumentiert. Sie können sie jederzeit bei Ihrer Behandelnden widerrufen.",
    introEyebrow: "Fragebogen",
    introDirect: (prac, patient) => ["Ausgefüllt in ", "der Sprechstunde", " durch ", prac, " für ", patient, "."],
    introRemote: (prac) => ["Gesendet von ", prac, "."],
    questionsCount: (n) => `${n} Frage${n > 1 ? "n" : ""}`,
    minutes: (m) => `~${m} Min`,
    resumeTitle: "Fragebogen fortsetzen",
    resumeBody: (n) => `Sie haben bereits ${n} Frage${n > 1 ? "n" : ""} beantwortet. Klicken Sie auf „Beginnen“, um dort fortzufahren, wo Sie aufgehört haben.`,
    pauseTipStrong: "Sie können jederzeit pausieren.",
    pauseTipBody: "Ihre Antworten werden automatisch gespeichert. Schließen Sie einfach diese Seite und öffnen Sie den Link in Ihrer E-Mail erneut, um fortzufahren.",
    cguEyebrow: "Nutzungsbedingungen",
    cguText: "Ich habe die Nutzungsbedingungen von Questiomed gelesen und akzeptiere sie.",
    readCgu: "Nutzungsbedingungen lesen →",
  },
  it: {
    previous: "Precedente",
    next: "Successivo",
    finishStep: "Termina",
    start: "Iniziare",
    submit: "Invia le mie risposte",
    submitting: "Invio in corso…",
    saveScore: "Salva punteggio",
    refuse: "Rifiuta",
    acceptAndContinue: "Accetto e continuo →",
    yes: "Sì",
    no: "No",
    minLabel: "Min",
    maxLabel: "Max",
    questionOf: (i, total) => `Domanda ${i} / ${total}`,
    lastStep: "Ultimo passaggio",
    almostThere: "Ci sei quasi!",
    saving: "Salvataggio",
    saveIntroDirect: (name) => `Clicca per salvare le risposte di ${name} nella sua cartella.`,
    submitIntro: "Clicca su «Invia le mie risposte» per trasmettere le tue risposte al tuo medico.",
    draftSaved: "Salvato · puoi chiudere e tornare più tardi",
    instructionsForThisQuestion: "Istruzioni per questa domanda",
    thanks: "Grazie!",
    thanksBody: "Le tue risposte sono state inviate al tuo medico.",
    completedAt: (dateStr, timeStr) => `Questionario completato il ${dateStr} alle ${timeStr}`,
    translating: "Traduzione in corso…",
    linkInvalid: "Link non valido",
    templateDeletedTitle: "Il tuo medico ha eliminato questo questionario",
    templateDeletedBody: "Questo link non è più attivo. Contatta il tuo medico se pensi si tratti di un errore — potrà inviarti un nuovo questionario.",
    consentRefusedTitle: "Consenso rifiutato",
    consentRefusedBody: "La tua scelta è stata registrata. Il tuo medico sarà informato e potrà contattarti in altro modo se necessario.",
    consentEyebrow: "Questionario medico",
    consentTitleSuffix: "ti invita a compilare un questionario",
    consentSubtitle: "Le tue risposte gli saranno trasmesse direttamente per preparare la tua presa in carico. Ci vogliono solo pochi minuti.",
    dataProtectedTitle: "I tuoi dati sanitari sono protetti",
    dataProtectedBody: "Sono ospitati in Francia presso un fornitore certificato HDS e sono accessibili solo al tuo medico.",
    consentCheckboxLabel: "Accetto la {privacy} e che le mie risposte siano trasmesse al mio medico per la mia presa in carico medica.",
    privacyLinkLabel: "politica dei dati personali",
    consentFooter: "Il tuo consenso è datato e tracciato in conformità al GDPR (Art. 7.1). Puoi ritirarlo in qualsiasi momento presso il tuo medico.",
    introEyebrow: "Questionario",
    introDirect: (prac, patient) => ["Compilato in ", "consultazione", " da ", prac, " per ", patient, "."],
    introRemote: (prac) => ["Inviato da ", prac, "."],
    questionsCount: (n) => `${n} domand${n > 1 ? "e" : "a"}`,
    minutes: (m) => `~${m} min`,
    resumeTitle: "Ripresa del questionario",
    resumeBody: (n) => `Hai già risposto a ${n} domand${n > 1 ? "e" : "a"}. Clicca su «Iniziare» per riprendere da dove hai lasciato.`,
    pauseTipStrong: "Puoi fare una pausa in qualsiasi momento.",
    pauseTipBody: "Le tue risposte vengono salvate automaticamente. Chiudi semplicemente questa pagina e riapri il link della tua email per riprendere.",
    cguEyebrow: "Condizioni generali d'uso",
    cguText: "Ho letto e accetto le condizioni generali d'uso di Questiomed.",
    readCgu: "Leggere le condizioni →",
  },
  pt: {
    previous: "Anterior",
    next: "Seguinte",
    finishStep: "Terminar",
    start: "Começar",
    submit: "Enviar as minhas respostas",
    submitting: "A enviar…",
    saveScore: "Guardar pontuação",
    refuse: "Recusar",
    acceptAndContinue: "Aceito e continuo →",
    yes: "Sim",
    no: "Não",
    minLabel: "Mín",
    maxLabel: "Máx",
    questionOf: (i, total) => `Pergunta ${i} / ${total}`,
    lastStep: "Última etapa",
    almostThere: "Está quase lá!",
    saving: "A guardar",
    saveIntroDirect: (name) => `Clique para guardar as respostas de ${name} no seu ficheiro.`,
    submitIntro: "Clique em «Enviar as minhas respostas» para transmitir as suas respostas ao seu médico.",
    draftSaved: "Guardado · pode fechar e voltar mais tarde",
    instructionsForThisQuestion: "Instruções para esta pergunta",
    thanks: "Obrigado!",
    thanksBody: "As suas respostas foram enviadas ao seu médico.",
    completedAt: (dateStr, timeStr) => `Questionário concluído a ${dateStr} às ${timeStr}`,
    translating: "A traduzir…",
    linkInvalid: "Ligação inválida",
    templateDeletedTitle: "O seu médico eliminou este questionário",
    templateDeletedBody: "Esta ligação já não está ativa. Contacte o seu médico se pensa que se trata de um erro — ele poderá enviar-lhe um novo questionário.",
    consentRefusedTitle: "Consentimento recusado",
    consentRefusedBody: "A sua escolha foi registada. O seu médico será notificado e poderá contactá-lo por outro meio, se necessário.",
    consentEyebrow: "Questionário médico",
    consentTitleSuffix: "convida-o a preencher um questionário",
    consentSubtitle: "As suas respostas ser-lhe-ão transmitidas diretamente para preparar o seu acompanhamento. Demora apenas alguns minutos.",
    dataProtectedTitle: "Os seus dados de saúde estão protegidos",
    dataProtectedBody: "Estão alojados em França num fornecedor certificado HDS e apenas são acessíveis ao seu médico.",
    consentCheckboxLabel: "Aceito a {privacy} e que as minhas respostas sejam transmitidas ao meu médico para o meu acompanhamento médico.",
    privacyLinkLabel: "política de dados pessoais",
    consentFooter: "O seu consentimento é datado e rastreado nos termos do RGPD (Art. 7.1). Pode retirá-lo a qualquer momento junto do seu médico.",
    introEyebrow: "Questionário",
    introDirect: (prac, patient) => ["Preenchido em ", "consulta", " por ", prac, " para ", patient, "."],
    introRemote: (prac) => ["Enviado por ", prac, "."],
    questionsCount: (n) => `${n} pergunta${n > 1 ? "s" : ""}`,
    minutes: (m) => `~${m} min`,
    resumeTitle: "Retomar o seu questionário",
    resumeBody: (n) => `Já respondeu a ${n} pergunta${n > 1 ? "s" : ""}. Clique em «Começar» para retomar de onde parou.`,
    pauseTipStrong: "Pode fazer uma pausa a qualquer momento.",
    pauseTipBody: "As suas respostas são guardadas automaticamente. Basta fechar esta página e reabrir a ligação do seu email para retomar.",
    cguEyebrow: "Termos gerais de utilização",
    cguText: "Li e aceito os termos gerais de utilização do Questiomed.",
    readCgu: "Ler os termos →",
  },
  ar: {
    previous: "السابق",
    next: "التالي",
    finishStep: "إنهاء",
    start: "البدء",
    submit: "إرسال إجاباتي",
    submitting: "جارٍ الإرسال…",
    saveScore: "حفظ النتيجة",
    refuse: "رفض",
    acceptAndContinue: "أوافق وأتابع ←",
    yes: "نعم",
    no: "لا",
    minLabel: "الأدنى",
    maxLabel: "الأقصى",
    questionOf: (i, total) => `السؤال ${i} / ${total}`,
    lastStep: "الخطوة الأخيرة",
    almostThere: "أوشكت على الانتهاء!",
    saving: "جارٍ الحفظ",
    saveIntroDirect: (name) => `انقر لحفظ إجابات ${name} في ملفه.`,
    submitIntro: "انقر على «إرسال إجاباتي» لإرسال إجاباتك إلى طبيبك.",
    draftSaved: "تم الحفظ · يمكنك الإغلاق والعودة لاحقًا",
    instructionsForThisQuestion: "تعليمات هذا السؤال",
    thanks: "شكرًا!",
    thanksBody: "تم إرسال إجاباتك إلى طبيبك بنجاح.",
    completedAt: (dateStr, timeStr) => `تم إكمال الاستبيان في ${dateStr} الساعة ${timeStr}`,
    translating: "جارٍ الترجمة…",
    linkInvalid: "رابط غير صالح",
    templateDeletedTitle: "قام طبيبك بحذف هذا الاستبيان",
    templateDeletedBody: "هذا الرابط لم يعد نشطًا. اتصل بطبيبك إذا كنت تعتقد أن ذلك خطأ — يمكنه أن يرسل لك استبيانًا جديدًا.",
    consentRefusedTitle: "تم رفض الموافقة",
    consentRefusedBody: "تم تسجيل اختيارك. سيتم إبلاغ طبيبك وقد يتصل بك بوسيلة أخرى إذا لزم الأمر.",
    consentEyebrow: "استبيان طبي",
    consentTitleSuffix: "يدعوك لملء استبيان",
    consentSubtitle: "سيتم إرسال إجاباتك مباشرة إليه لتحضير رعايتك. تستغرق العملية دقائق قليلة فقط.",
    dataProtectedTitle: "بياناتك الصحية محمية",
    dataProtectedBody: "يتم استضافتها في فرنسا لدى مزود معتمد HDS ولا يمكن الوصول إليها إلا من قِبل طبيبك.",
    consentCheckboxLabel: "أوافق على {privacy} وعلى نقل إجاباتي إلى طبيبي من أجل رعايتي الطبية.",
    privacyLinkLabel: "سياسة البيانات الشخصية",
    consentFooter: "يتم توثيق موافقتك بالتاريخ والوقت وفقًا للائحة العامة لحماية البيانات (المادة 7.1). يمكنك سحبها في أي وقت لدى طبيبك.",
    introEyebrow: "استبيان",
    introDirect: (prac, patient) => ["تم ملؤه في ", "الاستشارة", " من قبل ", prac, " لصالح ", patient, "."],
    introRemote: (prac) => ["أرسله ", prac, "."],
    questionsCount: (n) => `${n} سؤال`,
    minutes: (m) => `~${m} دقيقة`,
    resumeTitle: "استئناف الاستبيان",
    resumeBody: (n) => `لقد أجبت بالفعل على ${n} سؤال. انقر على «البدء» للاستئناف من حيث توقفت.`,
    pauseTipStrong: "يمكنك التوقف مؤقتًا في أي وقت.",
    pauseTipBody: "يتم حفظ إجاباتك تلقائيًا. أغلق هذه الصفحة وأعد فتح الرابط من بريدك للاستئناف.",
    cguEyebrow: "الشروط العامة للاستخدام",
    cguText: "لقد قرأت وقبلت الشروط العامة لاستخدام Questiomed.",
    readCgu: "قراءة الشروط ←",
  },
  zh: {
    previous: "上一步",
    next: "下一步",
    finishStep: "完成",
    start: "开始",
    submit: "发送我的回答",
    submitting: "发送中…",
    saveScore: "保存评分",
    refuse: "拒绝",
    acceptAndContinue: "我同意并继续 →",
    yes: "是",
    no: "否",
    minLabel: "最小",
    maxLabel: "最大",
    questionOf: (i, total) => `问题 ${i} / ${total}`,
    lastStep: "最后一步",
    almostThere: "就快完成了!",
    saving: "保存中",
    saveIntroDirect: (name) => `点击将 ${name} 的答案保存到病历。`,
    submitIntro: "点击「发送我的回答」将您的答案传送给您的医生。",
    draftSaved: "已保存 · 您可以关闭并稍后返回",
    instructionsForThisQuestion: "本问题的说明",
    thanks: "谢谢!",
    thanksBody: "您的回答已发送给您的医生。",
    completedAt: (dateStr, timeStr) => `问卷于 ${dateStr} ${timeStr} 完成`,
    translating: "翻译中…",
    linkInvalid: "链接无效",
    templateDeletedTitle: "您的医生已删除此问卷",
    templateDeletedBody: "此链接不再有效。如果您认为这是错误,请联系您的医生 — 他/她可以向您发送新的问卷。",
    consentRefusedTitle: "已拒绝同意",
    consentRefusedBody: "您的选择已被记录。您的医生将收到通知,如有必要可通过其他方式与您联系。",
    consentEyebrow: "医疗问卷",
    consentTitleSuffix: "邀请您填写一份问卷",
    consentSubtitle: "您的回答将直接传送给他/她以准备您的诊疗。仅需几分钟。",
    dataProtectedTitle: "您的健康数据受到保护",
    dataProtectedBody: "数据由法国 HDS 认证提供商托管,仅您的医生可以访问。",
    consentCheckboxLabel: "我接受{privacy},并同意将我的回答传送给我的医生用于医疗诊治。",
    privacyLinkLabel: "个人数据政策",
    consentFooter: "您的同意已按 GDPR 第 7.1 条打上时间戳并进行追踪。您可以随时通过您的医生撤回。",
    introEyebrow: "问卷",
    introDirect: (prac, patient) => ["由 ", prac, " 在", "诊室", "为 ", patient, " 填写。"],
    introRemote: (prac) => ["由 ", prac, " 发送。"],
    questionsCount: (n) => `${n} 个问题`,
    minutes: (m) => `约 ${m} 分钟`,
    resumeTitle: "继续填写您的问卷",
    resumeBody: (n) => `您已回答 ${n} 个问题。点击「开始」从上次停下的位置继续。`,
    pauseTipStrong: "您可以随时暂停。",
    pauseTipBody: "您的答案会自动保存。只需关闭此页面,然后重新打开邮件中的链接即可继续。",
    cguEyebrow: "使用条款",
    cguText: "我已阅读并接受 Questiomed 的使用条款。",
    readCgu: "阅读条款 →",
  },
};

// Fallback chain: locale → en → fr. Missing keys never break the render.
export function t(locale, key, ...args) {
  const chain = [locale, "en", "fr"];
  for (const l of chain) {
    const bucket = STRINGS[l];
    if (bucket && key in bucket) {
      const v = bucket[key];
      return typeof v === "function" ? v(...args) : v;
    }
  }
  return "";
}
